import { DateTime } from 'luxon';
import { EntityType, EventType, Event } from '../../../clients/quipli-service/events/events';
import { Order, OrderReturnStatus, OrderStatus, OrderType } from '../../../clients/quipli-service/order/order';
import { Timeline } from '../../../components/timeline/timeline';
import { useState, useEffect } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Product } from '../../../clients/quipli-service/product/product';
import { displayMoneyFormat } from '../../../util/util';
import { formatShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { Spinner } from '../../../components/spinner/spinner';
import { Location } from '../../../clients/quipli-service/location/location';
import { TaxCode } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import './order-history.scss';

export const OrderHistory = ({ order, location }: { order: Order; location: Location }) => {
  const { getEvents, getMultipleProductsByIds, getConfiguration } = useAPI();
  const { searchTaxCodes } = useTaxApiV1();
  const [events, setEvents] = useState<Event[]>();
  const [products, setProducts] = useState<Product[]>(order.products.map((p) => p.product));
  const [taxCodes, setTaxCodes] = useState<Pagination<TaxCode>>();
  const [trackingVia, setTrackingVia] = useState<string>();

  useEffect(() => {
    const init = async () => {
      const [_events, _trackingVia] = await Promise.all([
        getEvents(EntityType.Order, [order.id]),
        getConfiguration('CONF_UNIT_TRACKING_VIA'),
      ]);
      const _taxCodes = await searchTaxCodes({ ids: _events.flatMap((ev) => ev.metadata.taxCode) });
      setEvents(_events.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis()));
      const _products = await getMultipleProductsByIds(
        _events.flatMap((ev) =>
          ev.eventType === EventType.StatusUpdate && ev.metadata.products
            ? ev.metadata.products.map((p: any) => p.productId)
            : ev.metadata.products?.removed
            ? ev.metadata.products.removed.map((p: any) => p.productId)
            : [],
        ),
      );
      setProducts(_products.concat(order.products.map((p) => p.product)));
      setTaxCodes(_taxCodes);
      setTrackingVia(_trackingVia?.value === '1' ? 'serialNumber' : 'unitNumber');
    };
    init();
  }, [order]);

  const getProductName = (orderProductId: number) => {
    return order.products.find((p) => p.id === orderProductId)?.name || 'Unknown';
  };

  const getOrderStatusLabel = (
    status: [OrderStatus, OrderReturnStatus] | OrderStatus | OrderReturnStatus,
    prevStatus: [OrderStatus, OrderReturnStatus] | OrderStatus | OrderReturnStatus,
    orderType: OrderType,
    productStatus?: OrderReturnStatus,
  ): string => {
    if (orderType === OrderType.Sale) {
      switch (status instanceof Array ? status[0] : status) {
        case OrderStatus.Requested:
          return 'Requested';
        case OrderStatus.Approved:
          return 'Closed';
        case OrderStatus.Quote:
          return 'Quote';
        case OrderStatus.Cancelled:
          return 'Cancelled';
        default:
          return '';
      }
    } else if (status instanceof Array) {
      switch (status.join(',')) {
        case `${OrderStatus.Requested},${OrderReturnStatus.NotStarted}`:
          return 'Requested';
        case `${OrderStatus.Approved},${OrderReturnStatus.NotStarted}`:
          return 'Approved';
        case `${OrderStatus.Approved},${OrderReturnStatus.OnRent}`:
          return productStatus && productStatus !== OrderReturnStatus.OnRent
            ? getOrderStatusLabel(productStatus, OrderStatus.Approved, orderType, undefined)
            : 'On Rent';
        case `${OrderStatus.Approved},${OrderReturnStatus.OffRent}`:
          return productStatus && productStatus !== OrderReturnStatus.OffRent
            ? getOrderStatusLabel(productStatus, OrderStatus.Approved, orderType, undefined)
            : 'Off Rent';
        case `${OrderStatus.Approved},${OrderReturnStatus.Complete}`:
          if (
            (prevStatus instanceof Array && prevStatus[1] !== OrderReturnStatus.OffRent) ||
            prevStatus == OrderReturnStatus.OffRent
          )
            return 'Off Rent & Inventory Returned';
          else return 'Inventory Returned';
        case `${OrderStatus.Quote},${OrderReturnStatus.NotStarted}`:
          return 'Quote';
        default:
          if (status[0] === OrderStatus.Cancelled) return 'Cancelled';
          else return '';
      }
    } else {
      switch (status) {
        case OrderStatus.Requested:
          return 'Requested';
        case OrderStatus.Approved:
          return 'Approved';
        case OrderStatus.Quote:
          return 'Quote';
        case OrderStatus.Cancelled:
          return 'Cancelled';
        case OrderReturnStatus.NotStarted:
          return 'Requested';
        case OrderReturnStatus.OnRent:
          return 'On Rent';
        case OrderReturnStatus.OffRent:
          return 'Off Rent';
        case OrderReturnStatus.Complete:
          return 'Inventory Returned';
        default:
          return '';
      }
    }
  };

  const renderOrderUpdateEvent = (event: Event): { type: 'info'; content: any }[] => {
    try {
      let orderUpdateItems: { type: 'info'; content: JSX.Element; className?: string }[] = [
        {
          type: 'info',
          content: (
            <p className="mx-0 my-1">
              Order <span className="font-bold">Updated</span> by{' '}
              <span className="underline">{event.admin?.adminFullName}</span> (
              {event.timestamp.setZone(location.timezone).toFormat('LLL d, yyyy, hh:mm a')})
            </p>
          ),
        },
      ];
      if (event.metadata.products?.removed?.length) {
        orderUpdateItems = orderUpdateItems.concat(
          event.metadata.products.removed.map(
            (product: { productId: number; orderProductId: number; quantity: number; type: OrderType }) => {
              return {
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Product removed from order</span> -{' '}
                    {getProductName(product.orderProductId)}
                  </p>
                ),
              };
            },
          ),
        );
      }
      if (event.metadata.products?.added?.length) {
        orderUpdateItems = orderUpdateItems.concat(
          event.metadata.products.added.map(
            (product: {
              productId: number;
              orderProductId: number;
              quantity: number;
              type: OrderType;
              rentalStartDate?: string;
              rentalEndDate?: string;
              unitPrice?: number;
            }) => {
              return {
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Product added to order</span> - {getProductName(product.orderProductId)}
                    {product.rentalStartDate
                      ? ` - Rental period = ${DateTime.fromISO(product.rentalStartDate, { setZone: true }).toFormat(
                          'ff',
                        )} <b>to</b> ${
                          product.rentalEndDate
                            ? DateTime.fromISO(product.rentalEndDate, { setZone: true }).toFormat('ff')
                            : 'N/A'
                        }`
                      : ''}{' '}
                    Unit Price: {displayMoneyFormat(product.unitPrice, location.currency)}
                  </p>
                ),
              };
            },
          ),
        );
      }
      if (event.metadata.products?.updated?.length) {
        event.metadata.products.updated.map(
          (product: {
            productId: number;
            orderProductId: number;
            quantity?: number;
            rentalStartDate?: string;
            rentalEndDate?: string;
            unitPrice?: number;
            assignedUnits?: number[];
            unassignedUnits?: number[];
            fees?: {
              added?: { attributeId: number; amount: number; name: string }[];
              removed?: { attributeId: number; amount: number; name: string }[];
              updated?: { attributeId: number; amount: number; name: string }[];
            };
          }) => {
            if (product.quantity) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Product quantity changed</span> -{' '}
                    {getProductName(product.orderProductId)} x {product.quantity}
                  </p>
                ),
              });
            }
            if (product.rentalStartDate) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Rental period changed</span> - {getProductName(product.orderProductId)}{' '}
                    - New rental period = {DateTime.fromISO(product.rentalStartDate, { setZone: true }).toFormat('ff')}{' '}
                    <b>to</b>{' '}
                    {product.rentalEndDate
                      ? DateTime.fromISO(product.rentalEndDate, { setZone: true }).toFormat('ff')
                      : 'N/A'}
                  </p>
                ),
              });
            }

            if (product.unitPrice !== undefined) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Unit price changed</span> - {getProductName(product.orderProductId)} -
                    New unit price = {displayMoneyFormat(product.unitPrice)}
                  </p>
                ),
              });
            }

            if (product.assignedUnits?.length) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Units assigned to {getProductName(product.orderProductId)}</span>:{' '}
                    {product.assignedUnits.map((unit) => {
                      const pu = products
                        .find((p) => p.id === product.productId)
                        ?.inventoryUnits.find((pu) => pu.id === unit);
                      return (
                        <span key={unit}>
                          Unit {trackingVia === 'serialNumber' ? pu?.serialNumber : pu?.unitNumber}
                          <br />
                        </span>
                      );
                    })}
                  </p>
                ),
              });
            }
            if (product.unassignedUnits?.length) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Units unassigned from {getProductName(product.orderProductId)}</span>:{' '}
                    {product.unassignedUnits.map((unit) => {
                      const pu = products
                        .find((p) => p.id === product.productId)
                        ?.inventoryUnits.find((pu) => pu.id === unit);
                      return (
                        <span key={unit}>
                          Unit {trackingVia === 'serialNumber' ? pu?.serialNumber : pu?.unitNumber}
                          <br />
                        </span>
                      );
                    })}
                  </p>
                ),
              });
            }
            if (product.fees && product.fees.added?.length) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Product fee(s) added</span> -{' '}
                    {product.fees.added.map((fee) => (
                      <>
                        <span key={fee.attributeId}>
                          {getProductName(product.orderProductId)} - {fee.name} ={' '}
                          {displayMoneyFormat(fee.amount, location.currency)}
                        </span>
                        {product.fees!.added!.length ? <br /> : ''}
                      </>
                    ))}
                  </p>
                ),
              });
            }

            if (product.fees && product.fees.removed?.length) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Product fee(s) removed</span> -{' '}
                    {product.fees.removed.map((fee) => (
                      <>
                        <span key={fee.attributeId}>
                          {getProductName(product.orderProductId)} - {fee.name} ={' '}
                          {displayMoneyFormat(fee.amount, location.currency)}
                        </span>
                        {product.fees!.removed!.length ? <br /> : ''}
                      </>
                    ))}
                  </p>
                ),
              });
            }

            if (product.fees && product.fees.updated?.length) {
              orderUpdateItems = orderUpdateItems.concat({
                type: 'info',
                className: 'ml-10',
                content: (
                  <p className="mx-0 my-1">
                    <span className="font-bold">Product fee(s) updated</span> -{' '}
                    {product.fees.updated.map((fee) => (
                      <>
                        <span key={fee.attributeId}>
                          {getProductName(product.orderProductId)} - {fee.name} ={' '}
                          {displayMoneyFormat(fee.amount, location.currency)}
                        </span>{' '}
                        {product.fees!.updated!.length ? <br /> : ''}
                      </>
                    ))}
                  </p>
                ),
              });
            }
          },
        );
      }
      // contract line item changes
      if (event.metadata.damageWaiver !== undefined) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Damage waiver price changed</span> - New price ={' '}
              {displayMoneyFormat(event.metadata.damageWaiver)}
            </p>
          ),
        });
      }
      if (event.metadata.deliveryMethod !== undefined) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Delivery method changed</span> - New delivery method ={' '}
              {formatShippingType(event.metadata.deliveryMethod)}
            </p>
          ),
        });
      }
      if (event.metadata.deliveryFee !== undefined) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Delivery price changed</span> - New price ={' '}
              {displayMoneyFormat(event.metadata.deliveryFee)}
            </p>
          ),
        });
      }
      if (event.metadata.additionalDiscount !== undefined) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Additional discount added</span> - Discount amount ={' '}
              {displayMoneyFormat(event.metadata.additionalDiscount)}
            </p>
          ),
        });
      }
      if (event.metadata.taxPercentage !== undefined && !event.metadata.taxCode) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Tax percentage changed</span> - New tax percentage ={' '}
              <span className="font-semibold">{(event.metadata.taxPercentage.taxPercentage * 100).toFixed(4)}%</span>{' '}
              {event.metadata.taxPercentage.amount !== undefined
                ? `(new tax amount = ${displayMoneyFormat(event.metadata.taxPercentage.amount)})`
                : ''}
            </p>
          ),
        });
      }
      if (event.metadata.taxCode !== undefined) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Order tax code changed</span> - New tax code ={' '}
              <span className="font-semibold">
                {taxCodes!.items.find((tc) => tc.id === event.metadata.taxCode)?.name ?? 'N/A'} (
                {taxCodes!.items.find((tc) => tc.id === event.metadata.taxCode)?.totalPercentage}%){' '}
              </span>
            </p>
          ),
        });
      }
      if (event.metadata.coupon !== undefined) {
        orderUpdateItems = orderUpdateItems.concat({
          type: 'info',
          className: 'ml-10',
          content: (
            <p className="mx-0 my-1">
              <span className="font-bold">Coupon applied</span> - Coupon code = "{event.metadata.coupon}"
              {/* = {displayMoneyFormat(event.metadata.coupon.amount, location.currency)} */}
            </p>
          ),
        });
      }
      if (event.metadata.fees?.added?.length) {
        orderUpdateItems = orderUpdateItems.concat(
          event.metadata.fees.added.map((fee: { feeId: number; name: string; amount: number }) => {
            return {
              type: 'info',
              className: 'ml-10',
              content: (
                <p className="mx-0 my-1">
                  <span className="font-bold">Fee added</span> - {fee.name} = {displayMoneyFormat(fee.amount)}
                </p>
              ),
            };
          }),
        );
      }
      if (event.metadata.fees?.removed?.length) {
        orderUpdateItems = orderUpdateItems.concat(
          event.metadata.fees.removed.map((fee: { feeId: number; name: string; amount: number }) => {
            return {
              type: 'info',
              className: 'ml-10',
              content: (
                <p className="mx-0 my-1">
                  <span className="font-bold">Fee removed</span> - {fee.name} = {displayMoneyFormat(fee.amount)}
                </p>
              ),
            };
          }),
        );
      }
      if (event.metadata.fees?.updated?.length) {
        orderUpdateItems = orderUpdateItems.concat(
          event.metadata.fees.updated.map((fee: { feeId: number; name: string; amount: number }) => {
            return {
              type: 'info',
              className: 'ml-10',
              content: (
                <p className="mx-0 my-1">
                  <span className="font-bold">Fee updated</span> - {fee.name} = {displayMoneyFormat(fee.amount)}
                </p>
              ),
            };
          }),
        );
      }
      return orderUpdateItems;
    } catch (e: any) {
      console.error('Error rendering order history', e);
      return [];
    }
  };

  const renderOrderStatusUpdateEvent = (event: Event): { type: any; content: any }[] => {
    const statusLabel = getOrderStatusLabel(
      event.metadata.status,
      event.metadata.prevStatus,
      order.type,
      event.metadata.products[0]?.status,
    );
    return [
      {
        type:
          (event.metadata.status ?? '').includes('Cancelled') ||
          (event.metadata.status &&
            event.metadata.prevStatus &&
            orderStatusValue(event.metadata.status) < orderStatusValue(event.metadata.prevStatus))
            ? 'danger'
            : 'success',
        content: (
          <div>
            <p className="mx-0 my-1">
              Order{' '}
              <span className="font-bold">
                {orderStatusValue(event.metadata.status) < orderStatusValue(event.metadata.prevStatus)
                  ? 'Reverted to '
                  : ''}
                {statusLabel}
              </span>{' '}
              {event.admin ? (
                <>
                  by <span className="underline">{event.admin?.adminFullName}</span>
                </>
              ) : (
                ''
              )}
              {` (${event.timestamp.setZone(location.timezone).toFormat('LLL d, yyyy, hh:mm a')})`}{' '}
            </p>
          </div>
        ),
      },
      ...((event.metadata.products ?? []).map(
        (product: {
          productId: number;
          orderProductId: number;
          status: OrderReturnStatus;
          prevStatus?: OrderReturnStatus;
          statusTimestamp: string;
        }) => {
          return {
            type: 'success',
            className: 'ml-10',
            content: (
              <p className="mx-0 my-1">
                {getProductName(product.orderProductId)} -{' '}
                {product.status === OrderReturnStatus.OnRent
                  ? `Rental Start = ${DateTime.fromISO(product.statusTimestamp, { setZone: true })
                      .setZone(location.timezone)
                      .toFormat('ff')}`
                  : product.status === OrderReturnStatus.OffRent
                  ? `Rental End = ${DateTime.fromISO(product.statusTimestamp, { setZone: true })
                      .setZone(location.timezone)
                      .toFormat('ff')}`
                  : product.status === OrderReturnStatus.Complete && product.prevStatus === OrderReturnStatus.OnRent
                  ? `Off Rent & Inventory Returned = ${DateTime.fromISO(product.statusTimestamp, { setZone: true })
                      .setZone(location.timezone)
                      .toFormat('ff')}`
                  : `Inventory Returned = ${DateTime.fromISO(product.statusTimestamp, { setZone: true })
                      .setZone(location.timezone)
                      .toFormat('ff')}`}
              </p>
            ),
          };
        },
      ) ?? []),
    ];
  };

  const renderOrderCreatedEvent = (event: Event): { type: any; content: any }[] => {
    return [
      {
        type: 'success',
        content: (
          <div>
            <p className="mx-0 my-1">
              Order <span className="font-bold">Created</span> by{' '}
              <span className="underline">{event.admin?.adminFullName}</span> (
              {event.timestamp.setZone(location.timezone).toFormat('LLL d, yyyy, hh:mm a')})
            </p>
          </div>
        ),
      },
      ...event.metadata.products.map(
        (product: {
          productId: number;
          orderProductId: number;
          quantity: number;
          type: OrderType;
          rentalStartDate?: string;
          rentalEndDate?: string;
          unitPrice?: number;
        }) => {
          return {
            type: 'success',
            className: 'ml-10',
            content: (
              <p className="mx-0 my-1">
                {getProductName(product.orderProductId)}
                {product.quantity > 1 ? ` x ${product.quantity}` : ''} -{' '}
                {product.type === OrderType.Rental && product.rentalStartDate
                  ? `Rental start = ${DateTime.fromISO(product.rentalStartDate, { setZone: true }).toFormat('ff')}`
                  : ''}{' '}
                {product.type === OrderType.Rental && product.rentalEndDate ? (
                  <>
                    <b> to </b>
                    {DateTime.fromISO(product.rentalEndDate, { setZone: true }).toFormat('ff')}
                  </>
                ) : (
                  <></>
                )}{' '}
                {product.unitPrice ? displayMoneyFormat(product.unitPrice, location.currency) : ''}
              </p>
            ),
          };
        },
      ),
    ];
  };

  const orderStatusTransitionRanking: [OrderStatus, OrderReturnStatus][] = [
    [OrderStatus.Requested, OrderReturnStatus.NotStarted],
    [OrderStatus.Quote, OrderReturnStatus.NotStarted],
    [OrderStatus.Approved, OrderReturnStatus.NotStarted],
    [OrderStatus.Approved, OrderReturnStatus.OnRent],
    [OrderStatus.Approved, OrderReturnStatus.OffRent],
    [OrderStatus.Approved, OrderReturnStatus.Complete],
  ];
  // *** first status update events only recorded one status, so we need to handle that
  function orderStatusValue(status: [OrderStatus, OrderReturnStatus] | OrderReturnStatus | OrderStatus) {
    if (status instanceof Array) {
      return status[0] === OrderStatus.Cancelled
        ? 6
        : orderStatusTransitionRanking.findIndex((s) => s[0] === status[0] && s[1] === status[1]);
    } else {
      return status === OrderStatus.Cancelled
        ? 6
        : [OrderStatus.Approved, OrderStatus.Quote, OrderStatus.Requested].includes(status as any)
        ? 1
        : orderStatusTransitionRanking.findIndex((s) => s[1] === status);
    }
  }

  return events && taxCodes ? (
    <div className="order-history">
      <Timeline
        items={(events ?? []).flatMap((event) => {
          switch (event.eventType) {
            case EventType.StatusUpdate:
              return renderOrderStatusUpdateEvent(event);
            case EventType.OrderUpdate:
              return renderOrderUpdateEvent(event);
            case EventType.OrderCreated:
              return renderOrderCreatedEvent(event);
            default:
              return [];
          }
        })}
      />
    </div>
  ) : (
    <Spinner />
  );
};
